import React, { useEffect } from 'react';
import { Button, Drawer, Select, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { QUERY_PICKUP_SITE_DETAIL } from '../../../graphql/queries/dashboard/queryPickupSiteDetail';
import {
    QueryPickupSiteDetailResponse,
    PickupSiteDetailItem,
} from '../../../graphql/types/dashboard/PickupDashboardTypes';
import { GENERATE_PICKUP_SITE_DETAIL_DOWNLOAD } from '../../../graphql/mutations/dashboard/generatePickupSiteDetailDownloadReport';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';

interface PickupSiteDrawerProps {
    visible: boolean;
    onClose: () => void;
    startDate: string;
    endDate: string;
    hubIds?: number[];
    customerIds?: number[];
    ewrType?: string;
    bizDate?: string;
    drillFilter?: Record<string, number>;
}

const PAGE_SIZE = 20;

const PickupSiteDrawer: React.FC<PickupSiteDrawerProps> = ({
    visible,
    onClose,
    startDate,
    endDate,
    hubIds,
    customerIds,
    ewrType,
    bizDate,
    drillFilter,
}) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = React.useState(1);
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [downloadModalVisible, setDownloadModalVisible] = React.useState(false);
    const [downloadFileName, setDownloadFileName] = React.useState('');

    const [filterConnectResultList, setFilterConnectResultList] = React.useState<string[]>([]);

    const { effectiveStart, effectiveEnd } = React.useMemo(() => {
        const capEnd = (e: string) => e > endDate ? endDate : e;
        if (!bizDate) return { effectiveStart: startDate, effectiveEnd: endDate };
        if (bizDate.includes('~')) {
            const [s, e] = bizDate.split('~');
            return { effectiveStart: s.trim(), effectiveEnd: capEnd(e.trim()) };
        }
        if (/^\d{4}-\d{2}$/.test(bizDate)) {
            const start = bizDate + '-01';
            const end = dayjs(`${bizDate}-01`).endOf('month').format('YYYY-MM-DD');
            return { effectiveStart: start, effectiveEnd: capEnd(end) };
        }
        return { effectiveStart: bizDate, effectiveEnd: bizDate };
    }, [bizDate, startDate, endDate]);

    const buildVariables = React.useCallback((page: number) => ({
        input: {
            startDate: effectiveStart,
            endDate: effectiveEnd,
            hubIds: hubIds && hubIds.length > 0 ? hubIds : undefined,
            customerIds: customerIds && customerIds.length > 0 ? customerIds : undefined,
            ewrType: ewrType ?? undefined,
            page,
            pageSize: PAGE_SIZE,
            isCleaned: drillFilter?.isCleaned ?? undefined,
            connectResultList: filterConnectResultList.length > 0 ? filterConnectResultList : undefined,
        },
    }), [effectiveStart, effectiveEnd, hubIds, customerIds, ewrType, drillFilter, filterConnectResultList]);

    const [fetchDetail, { data, loading }] = useLazyQuery<QueryPickupSiteDetailResponse>(
        QUERY_PICKUP_SITE_DETAIL
    );

    const [generateReport] = useMutation(GENERATE_PICKUP_SITE_DETAIL_DOWNLOAD, {
        onCompleted: (resData) => {
            const fileName = resData.generatePickupSiteDetailDownloadReport;
            if (fileName) {
                setDownloadFileName(fileName);
                setDownloadModalVisible(true);
            } else {
                void message.error('导出失败，请稍后重试');
            }
        },
        onError: () => { void message.error('导出失败，请稍后重试'); },
    });

    useEffect(() => {
        if (!visible) return;
        setCurrentPage(1);
        setFilterConnectResultList([]);
        fetchDetail({
            variables: {
                input: {
                    startDate: effectiveStart,
                    endDate: effectiveEnd,
                    hubIds: hubIds && hubIds.length > 0 ? hubIds : undefined,
                    customerIds: customerIds && customerIds.length > 0 ? customerIds : undefined,
                    ewrType: ewrType ?? undefined,
                    page: 1,
                    pageSize: PAGE_SIZE,
                    isCleaned: drillFilter?.isCleaned ?? undefined,
                    connectResultList: undefined,
                },
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, effectiveStart, effectiveEnd, hubIds, customerIds, ewrType, drillFilter]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchDetail({ variables: buildVariables(page) });
    };

    const handleConnectResultChange = (values: string[]) => {
        setFilterConnectResultList(values);
        setCurrentPage(1);
        fetchDetail({
            variables: {
                input: {
                    startDate: effectiveStart,
                    endDate: effectiveEnd,
                    hubIds: hubIds && hubIds.length > 0 ? hubIds : undefined,
                    customerIds: customerIds && customerIds.length > 0 ? customerIds : undefined,
                    ewrType: ewrType ?? undefined,
                    page: 1,
                    pageSize: PAGE_SIZE,
                    isCleaned: drillFilter?.isCleaned ?? undefined,
                    connectResultList: values.length > 0 ? values : undefined,
                },
            },
        });
    };

    const handleDownload = () => {
        void runExportWithCooldown(async () => {
            await generateReport({
                variables: {
                    ...buildVariables(1),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            });
        });
    };

    const result = data?.queryPickupSiteDetail;
    const dataSource = result?.content ?? [];
    const total = result?.total ?? 0;

    const statusConfig: Record<number, { label: string; color: string }> = {
        1: { label: t('enum.cooperationWarehouseStatus.active'),    color: '#52c41a' },
        0: { label: t('enum.cooperationWarehouseStatus.suspended'), color: '#ff4d4f' },
        2: { label: t('enum.cooperationWarehouseStatus.pending'),   color: '#fa8c16' },
    };

    const formatDate = (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY/MM/DD HH:mm:ss') : '-';

    const columns: ColumnsType<PickupSiteDetailItem> = [
        { title: t('dashboard.pickup.drawer.siteId'), dataIndex: 'siteId', key: 'siteId', width: 100, fixed: 'left' },
        { title: t('dashboard.pickup.drawer.name'), dataIndex: 'name', key: 'name', width: 160 },
        { title: '建联结果', dataIndex: 'connectResult', key: 'connectResult', width: 120, render: (v: string) => v || '-' },
        { title: t('dashboard.pickup.drawer.hub'), dataIndex: 'hubName', key: 'hubName', width: 120 },
        { title: t('dashboard.pickup.drawer.city'), dataIndex: 'city', key: 'city', width: 100 },
        { title: t('dashboard.pickup.drawer.state'), dataIndex: 'state', key: 'state', width: 80 },
        { title: t('dashboard.pickup.drawer.zipcode'), dataIndex: 'zipcode', key: 'zipcode', width: 100 },
        { title: t('dashboard.pickup.drawer.address'), dataIndex: 'addressLine1', key: 'addressLine1', width: 200 },
        { title: t('dashboard.pickup.drawer.contactName'), dataIndex: 'contactName', key: 'contactName', width: 120 },
        {
            title: t('dashboard.pickup.drawer.contactPhone'),
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 150,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
        },
        { title: t('dashboard.pickup.drawer.vehicleType'), dataIndex: 'vehicleType', key: 'vehicleType', width: 110 },
        { title: t('dashboard.pickup.drawer.continuousFailure'), dataIndex: 'continuousFailure', key: 'continuousFailure', width: 100, render: (v: boolean | null) => v === null || v === undefined ? '-' : (v ? '是' : '否') },
        { title: t('dashboard.pickup.drawer.standingPickup'), dataIndex: 'standingPickup', key: 'standingPickup', width: 110, render: (v: boolean | null) => v === null || v === undefined ? '-' : (v ? '是' : '否') },
        {
            title: t('dashboard.pickup.drawer.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (v: number | null) => {
                if (v === null || v === undefined) return <span>-</span>;
                const cfg = statusConfig[v];
                return cfg
                    ? <Tag color={cfg.color} style={{ whiteSpace: 'nowrap' }}>{cfg.label}</Tag>
                    : <span>{v}</span>;
            },
        },
        {
            title: t('dashboard.pickup.drawer.remark'),
            dataIndex: 'remark',
            key: 'remark',
            width: 200,
            render: (v: string | null) => v || '-',
        },
        {
            title: t('dashboard.pickup.drawer.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{formatDate(v)}</span>,
        },
        {
            title: t('dashboard.pickup.drawer.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 170,
            render: (v: string | null) => <span style={{ whiteSpace: 'nowrap' }}>{formatDate(v)}</span>,
        },
    ];

    const drawerTitle = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
                {t('dashboard.pickup.drawer.siteTitle')}
                {effectiveStart && `（${effectiveStart}${effectiveStart !== effectiveEnd ? ` ~ ${effectiveEnd}` : ''}）`}
            </span>
            <Button
                type="primary"
                size="small"
                style={{ marginRight: 32 }}
                loading={exportLoading}
                disabled={exportLoading}
                onClick={handleDownload}
            >
                {getExportButtonLabel('下载')}
            </Button>
        </div>
    );

    return (
        <Drawer
            title={drawerTitle}
            open={visible}
            onClose={onClose}
            width="90vw"
            destroyOnClose
        >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="建联结果"
                    style={{ minWidth: 200 }}
                    value={filterConnectResultList}
                    onChange={handleConnectResultChange}
                    maxTagCount="responsive"
                    options={[
                        { value: '已建联',         label: '已建联' },
                        { value: '确认无法建联',    label: '确认无法建联' },
                        { value: '揽收成功待建联',  label: '揽收成功待建联' },
                        { value: '揽收失败待建联',  label: '揽收失败待建联' },
                        { value: '上门中',         label: '上门中' },
                        { value: '待上门',         label: '待上门' },
                    ]}
                />
                <Button
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                        setFilterConnectResultList([]);
                        setCurrentPage(1);
                        fetchDetail({
                            variables: {
                                input: {
                                    startDate: effectiveStart,
                                    endDate: effectiveEnd,
                                    hubIds: hubIds && hubIds.length > 0 ? hubIds : undefined,
                                    customerIds: customerIds && customerIds.length > 0 ? customerIds : undefined,
                                    page: 1,
                                    pageSize: PAGE_SIZE,
                                    isCleaned: drillFilter?.isCleaned ?? undefined,
                                    connectResultList: undefined,
                                },
                            },
                        });
                    }}
                >
                    重置
                </Button>
            </div>
            <Table<PickupSiteDetailItem>
                rowKey="siteId"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                scroll={{ x: 2100 }}
                pagination={{
                    current: currentPage,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: handlePageChange,
                    showTotal: (t2) => `共 ${t2} 条`,
                }}
                size="small"
            />
            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={downloadFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.pickupSiteDetail}
            />
        </Drawer>
    );
};

export default PickupSiteDrawer;
